import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { computeEffectivePermissions } from '../config/modulePermissions';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global axios response interceptor: when the backend rejects a request because
// the JWT has expired / been tampered with / signed with an old secret, the
// FastAPI auth dependency returns 401 with detail "Could not validate
// credentials". Without intervention this surfaces as a cryptic banner on
// whichever page the user happened to be on. Catch it once here, wipe the bad
// token, and bounce the user to /login with a clean reason. This is a
// cross-cutting fix; it also benefits every other authenticated page.
let _interceptorInstalled = false;
const _installAuthInterceptor = () => {
  if (_interceptorInstalled) return;
  _interceptorInstalled = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const url = error?.config?.url || '';
      const isAuthEndpoint = /\/auth\/(login|verify-otp|register|request-password-reset|reset-password|verify-email|switch-amc)\b/.test(url);
      const looksLikeJwtFailure =
        status === 401 &&
        typeof detail === 'string' &&
        /could not validate credentials|not authenticated|token has expired/i.test(detail);
      if (looksLikeJwtFailure && !isAuthEndpoint) {
        try {
          localStorage.removeItem('token');
          localStorage.setItem('session_expired_reason', detail);
          delete axios.defaults.headers.common['Authorization'];
        } catch { /* ignore */ }
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          // Use a hard navigation so React state + axios defaults are fully reset.
          const here = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/login?session_expired=1&next=${here}`;
        }
      }
      return Promise.reject(error);
    },
  );
};
_installAuthInterceptor();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const enrichUserWithEffectivePerms = async (userData) => {
    if (userData.user_type === 'amc_user' && userData.amc_id) {
      try {
        const amcRes = await axios.get(`${API_URL}/amcs/me`);
        const amcModules = amcRes.data?.enabled_modules || [];
        const effective = computeEffectivePermissions(userData.module_permissions, amcModules);
        return {
          ...userData,
          effective_permissions: effective,
          amc_enabled_modules: amcModules,
          // Preferences the frontend needs everywhere (formatters, branding, …)
          amc_date_format: amcRes.data?.date_format || 'DD-MMMM-YYYY',
          amc_name: amcRes.data?.name,
        };
      } catch {
        return { ...userData, effective_permissions: userData.module_permissions || {}, amc_enabled_modules: [], amc_date_format: 'DD-MMMM-YYYY' };
      }
    }
    return { ...userData, effective_permissions: userData.module_permissions || {}, amc_enabled_modules: [], amc_date_format: 'DD-MMMM-YYYY' };
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      const enriched = await enrichUserWithEffectivePerms(response.data);
      setUser(enriched);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    return response.data;
  };

  const verifyOTP = async (email, otp, amc_id = null) => {
    const payload = { email, otp };
    if (amc_id) payload.amc_id = amc_id;
    const response = await axios.post(`${API_URL}/auth/verify-otp`, payload);
    
    // Check if AMC selection is needed
    if (response.data.requires_amc_selection) {
      return response.data; // { requires_amc_selection: true, amc_list: [...] }
    }
    
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    const enriched = await enrichUserWithEffectivePerms(userData);
    setUser(enriched);
    return response.data;
  };

  const switchAMC = async (amc_id) => {
    const response = await axios.post(`${API_URL}/auth/switch-amc`, { amc_id });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    const enriched = await enrichUserWithEffectivePerms(userData);
    setUser(enriched);
    return response.data;
  };

  const verifyEmail = async (token) => {
    const response = await axios.post(`${API_URL}/auth/verify-email?token=${token}`);
    return response.data;
  };

  const requestPasswordReset = async (email) => {
    const response = await axios.post(`${API_URL}/auth/request-password-reset`, { email });
    return response.data;
  };

  const resetPassword = async (token, new_password) => {
    const response = await axios.post(`${API_URL}/auth/reset-password`, { token, new_password });
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { access_token, user: regUser } = response.data;
    setToken(access_token);
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    const enriched = await enrichUserWithEffectivePerms(regUser);
    setUser(enriched);
    return response.data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    loading,
    login,
    verifyOTP,
    switchAMC,
    verifyEmail,
    requestPasswordReset,
    resetPassword,
    register,
    logout,
    isAuthenticated: !!token,
    fetchCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
